import { Component, Input, ViewChild, AfterViewInit } from '@angular/core';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import {DatePipe, DecimalPipe, NgIf} from '@angular/common';

export interface ConsumptionData {
  date: Date | string;
  meter_amount: number | null;
  meter_line_color?: string | null;

  // (kommt vom Mapper, wird hier NICHT benutzt – wir berechnen selbst)
  consumption?: number | null;
  consumption_line_color?: string | null;

  flow_temperature?: number | null;
  flow_line_color?: string | null;

  outside_temperature?: number | null;
  outside_line_color?: string | null;

  prev_year_consumption?: number | null;
}

// Interne Zeilen mit berechnetem Verbrauch & Vorjahr
type TableRow = ConsumptionData & {
  _computedConsumption: number | null;
  _computedPrevYear: number | null;
};

@Component({
  selector: 'app-consumption-table',
  standalone: true,
  imports: [MatTableModule, MatSortModule, DatePipe, DecimalPipe, NgIf],
  templateUrl: './consumption-table.html',
  styleUrl: './consumption-table.scss'
})
export class ConsumptionTable implements AfterViewInit {
  displayedColumns: string[] = ['date', 'meter_amount', 'outside_temperature', 'consumption', 'prev_year_consumption'];

  dataSource = new MatTableDataSource<TableRow>([]);

  @ViewChild(MatSort) sort!: MatSort;

  @Input() set data(value: ConsumptionData[] | null | undefined) {
    const rows = (value ?? []).slice();

    // Chronologisch sortieren (aufsteigend)
    rows.sort((a, b) => {
      const ta = a.date instanceof Date ? a.date.getTime() : new Date(a.date).getTime();
      const tb = b.date instanceof Date ? b.date.getTime() : new Date(b.date).getTime();
      return ta - tb;
    });

    // Verbrauch wie im Chart: Vorwärts-Differenz aus meter_amount; letzter Punkt = null
    const tableRows: TableRow[] = rows.map((r, i, arr) => {
      const curr = typeof r.meter_amount === 'number' ? r.meter_amount : null;
      const next = typeof arr[i + 1]?.meter_amount === 'number' ? (arr[i + 1]!.meter_amount as number) : null;

      let diff: number | null = null;
      if (i < arr.length - 1 && curr !== null && next !== null) {
        const d = next - curr;
        diff = d >= 0 ? round1(d) : null;
      }

      const prevYear = r.prev_year_consumption ?? null;

      return {
        ...r,
        _computedConsumption: diff,
        _computedPrevYear: prevYear
      };
    });

    this.dataSource.data = tableRows;
  }

  ngAfterViewInit(): void {
    // Numerische Sortierung inkl. berechneter Felder
    this.dataSource.sortingDataAccessor = (item: TableRow, property: string) => {
      switch (property) {
        case 'date': {
          const v: any = item.date;
          const ms = v instanceof Date ? v.getTime() : new Date(v).getTime();
          return isNaN(ms) ? 0 : ms;
        }
        case 'meter_amount':
          return typeof item.meter_amount === 'number' ? item.meter_amount : Number.NEGATIVE_INFINITY;
        case 'outside_temperature':
          return typeof item.outside_temperature === 'number' ? item.outside_temperature : Number.NEGATIVE_INFINITY;
        case 'consumption':
          return typeof item._computedConsumption === 'number' ? item._computedConsumption : Number.NEGATIVE_INFINITY;
        case 'prev_year_consumption':
          return typeof item._computedPrevYear === 'number' ? item._computedPrevYear : Number.NEGATIVE_INFINITY;
        default:
          return (item as any)[property];
      }
    };

    this.dataSource.sort = this.sort;
  }
}

function round1(n: number): number {
  return Math.round((n + Number.EPSILON) * 10) / 10;
}
